import { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { analyticsService } from "../services/analyticsService";
import { PermissionDenied } from "../components/ui/PermissionDenied";
import { useAuth } from "../contexts/AuthContext";
import {
  Users,
  BookOpen,
  DollarSign,
  Clock,
  TrendingUp,
  TrendingDown,
  Eye,
  GraduationCap,
  Infinity,
  Calendar,
  RefreshCw,
  BarChart3,
  Activity,
  Target,
} from "lucide-react";

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  showAllTime?: boolean;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
}

function StatCard({
  title,
  value,
  change,
  showAllTime,
  icon: Icon,
  iconColor,
  iconBg,
}: StatCardProps) {
  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {showAllTime ? (
            <div className="flex items-center gap-1 mt-2 text-sm text-gray-500">
              <Infinity size={14} />
              <span>All-time totals</span>
            </div>
          ) : (
            change !== undefined && (
              <div
                className={`flex items-center gap-1 mt-2 text-sm ${
                  change >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {change >= 0 ? (
                  <TrendingUp size={14} />
                ) : (
                  <TrendingDown size={14} />
                )}
                <span>
                  {change >= 0 ? "+" : ""}
                  {change}%
                </span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            )
          )}
        </div>
        <div className={`p-3 rounded-xl ${iconBg}`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
    </Card>
  );
}

interface TopItem {
  _id: string;
  title?: string;
  name?: string;
  count: number;
  watchTime?: number;
  uniqueViewers?: number;
  averageProgress?: number;
  courseTitle?: string;
}

export default function AnalyticsPage() {
  const { hasPermission } = useAuth();

  if (!hasPermission('analytics:read')) {
    return <PermissionDenied requiredPermission="analytics:read" />;
  }

  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState("month");
  // 'cumulative' (UI) maps to 'all' (wire) per backend convention
  // (see getMostWatchedLessons / getMostActiveStudents in analyticsController.js).
  const isCumulative = dateRange === "cumulative";
  const wirePeriod = isCumulative ? "all" : dateRange;
  const [summary, setSummary] = useState<any>(null);
  const [revenue, setRevenue] = useState<any>(null);
  const [topLessons, setTopLessons] = useState<TopItem[]>([]);
  const [topStudents, setTopStudents] = useState<TopItem[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    try {
      const [summaryRes, revenueRes, lessonsRes, studentsRes, monthlyRes] =
        await Promise.all([
          analyticsService.getDashboardSummary({ period: wirePeriod }),
          analyticsService.getRevenueStatistics({ period: wirePeriod }),
          analyticsService.getMostWatchedLessons({ limit: 5, period: wirePeriod }),
          analyticsService.getMostActiveStudents({ limit: 5, period: wirePeriod }),
          analyticsService.getMonthlyEnrollments({ months: 6 }),
        ]);

      setSummary(summaryRes.summary || summaryRes);
      setRevenue(revenueRes.revenue || revenueRes);

      const lessonsData = lessonsRes.data || lessonsRes.lessons || [];
      const mappedLessons: TopItem[] = lessonsData.map((lesson: any) => ({
        _id: lesson.lessonId || lesson._id,
        title: lesson.lessonTitle || lesson.title,
        name: lesson.lessonTitle || lesson.title,
        count: lesson.uniqueViewers || 0,
        watchTime: lesson.totalWatchTimeMinutes || lesson.watchTime,
        uniqueViewers: lesson.uniqueViewers,
        averageProgress: lesson.averageProgress,
        courseTitle: lesson.courseTitle,
      }));
      setTopLessons(mappedLessons);

      const studentsData = studentsRes.data || studentsRes.students || [];
      const mappedStudents: TopItem[] = studentsData.map((student: any) => ({
        _id: student.userId || student._id,
        name: student.username || student.name || "Student",
        count: student.lessonsWatched || student.count || 0,
        watchTime: student.totalWatchTimeMinutes || student.watchTime,
      }));
      setTopStudents(mappedStudents);

      setMonthlyData(monthlyRes.data || []);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatMonth = (year: number, month: number) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]} '${year.toString().slice(-2)}`;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-500 mt-1">
              Track your platform performance and insights
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {["week", "month", "quarter", "year", "cumulative"].map((period) => (
              <Button
                key={period}
                variant={dateRange === period ? "primary" : "outline"}
                size="sm"
                onClick={() => setDateRange(period)}
                className="capitalize"
              >
                {period}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={fetchAnalytics}>
              <RefreshCw size={16} />
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        {isCumulative && (
          <div className="flex items-center gap-2 mb-3 text-sm text-gray-500 italic">
            <Infinity size={14} className="text-gray-400" />
            <span>All data below represents lifetime totals from platform launch.</span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={summary?.totalUsers || 0}
            change={isCumulative ? undefined : summary?.userGrowth}
            showAllTime={isCumulative}
            icon={Users}
            iconColor="text-blue-600"
            iconBg="bg-blue-100"
          />
          <StatCard
            title="Total Enrollments"
            value={summary?.totalEnrollments || 0}
            change={isCumulative ? undefined : summary?.enrollmentGrowth}
            showAllTime={isCumulative}
            icon={GraduationCap}
            iconColor="text-green-600"
            iconBg="bg-green-100"
          />
          <StatCard
            title="Total Revenue"
            value={`$${(revenue?.total || 0).toLocaleString()}`}
            change={isCumulative ? undefined : revenue?.growth}
            showAllTime={isCumulative}
            icon={DollarSign}
            iconColor="text-amber-600"
            iconBg="bg-amber-100"
          />
          <StatCard
            title="Watch Time"
            value={formatDuration(summary?.totalWatchTime || 0)}
            change={isCumulative ? undefined : summary?.watchTimeGrowth}
            showAllTime={isCumulative}
            icon={Clock}
            iconColor="text-purple-600"
            iconBg="bg-purple-100"
          />
        </div>

        {/* Charts Section */}
        {!isCumulative && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Enrollments Chart */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Enrollment Trends
              </h3>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-64 relative">
              {monthlyData.length > 0 ? (
                <>
                  <div className="absolute inset-0 flex items-end justify-between gap-4 px-4 pb-8">
                    {monthlyData.map((item, index) => {
                      const maxValue = Math.max(
                        ...monthlyData.map((d) => d.count || 0)
                      );
                      const heightPercent =
                        maxValue > 0 ? ((item.count || 0) / maxValue) * 100 : 0;
                      const count = item.count || 0;
                      return (
                        <div
                          key={index}
                          className="flex flex-col items-center justify-end flex-1 min-w-0 h-full relative"
                        >
                          {count > 0 && (
                            <div
                              className="w-11/12 max-w-16 bg-gradient-to-t from-indigo-600 via-indigo-500 to-indigo-400 rounded-t-lg transition-all duration-300 hover:from-indigo-700 hover:via-indigo-600 hover:to-indigo-500 cursor-pointer relative group shadow-lg shadow-indigo-200"
                              style={{ height: `${Math.max(heightPercent, 0)}%` }}
                            >
                              <div className="absolute inset-0 bg-gradient-to-b from-white/0 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                              <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap z-20 font-medium">
                                {count} enrollments
                              </div>
                            </div>
                          )}
                          <span className="text-xs text-gray-600 mt-2 text-center truncate w-full font-medium">
                            {formatMonth(item.year, item.month)}
                          </span>
                          <span className="text-sm font-bold text-gray-900 text-center">
                            {count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200" />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No data available
                </div>
              )}
            </div>
          </Card>

          {/* Revenue Chart */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Revenue Overview
              </h3>
              <Activity className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">This Period</p>
                    <p className="text-xl font-bold text-gray-900">
                      ${(revenue?.current || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Last Period</p>
                    <p className="text-xl font-bold text-gray-900">
                      ${(revenue?.previous || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Lifetime Total</p>
                    <p className="text-xl font-bold text-gray-900">
                      ${(revenue?.total || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
        )}

        {/* Top Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Watched Lessons */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Most Watched Lessons
              </h3>
              <Eye className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              {topLessons.length > 0 ? (
                topLessons.map((lesson, index) => (
                  <div
                    key={lesson._id}
                    className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg"
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                        index === 0
                          ? "bg-amber-100 text-amber-600"
                          : index === 1
                          ? "bg-gray-100 text-gray-600"
                          : index === 2
                          ? "bg-orange-100 text-orange-600"
                          : "bg-gray-50 text-gray-500"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {lesson.title || lesson.name}
                      </p>
                      <div className="text-sm text-gray-500">
                        {lesson.courseTitle && (
                          <span className="inline-block mr-2">
                            {lesson.courseTitle}
                          </span>
                        )}
                        {lesson.count} views
                        {lesson.averageProgress && (
                          <span className="inline-block ml-2">
                            • {lesson.averageProgress.toFixed(0)}% avg progress
                          </span>
                        )}
                        {lesson.watchTime &&
                          ` • ${formatDuration(lesson.watchTime)}`}
                      </div>
                    </div>
                    <BookOpen className="w-4 h-4 text-gray-400" />
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">No data yet</p>
              )}
            </div>
          </Card>

          {/* Most Active Students */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Most Active Students
              </h3>
              <Users className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              {topStudents.length > 0 ? (
                topStudents.map((student, index) => (
                  <div
                    key={student._id}
                    className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg"
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                        index === 0
                          ? "bg-amber-100 text-amber-600"
                          : index === 1
                          ? "bg-gray-100 text-gray-600"
                          : index === 2
                          ? "bg-orange-100 text-orange-600"
                          : "bg-gray-50 text-gray-500"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {student.name || "Student"}
                      </p>
                      <div className="text-sm text-gray-500">
                        {student.count} lessons watched
                        {student.watchTime &&
                          ` • ${formatDuration(student.watchTime)}`}
                      </div>
                    </div>
                    <GraduationCap className="w-4 h-4 text-gray-400" />
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">No data yet</p>
              )}
            </div>
          </Card>
        </div>

        {/* Platform Stats */}
        <Card className="p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            Platform Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">
                {summary?.totalCourses || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">Total Courses</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">
                {summary?.totalLessons || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">Total Lessons</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">
                {summary?.totalInstructors || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">Instructors</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">
                {summary?.completionRate || 0}%
              </p>
              <p className="text-sm text-gray-500 mt-1">Completion Rate</p>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
